import { Collapse } from 'antd';
import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import InfoIcon from 'src/shared/assets/icons/info-i.svg';
import DropdownIcon from 'src/shared/assets/icons/triangle.svg';
import type { Directories } from 'src/store/directories/directories.store';

import type { FormStore } from '../entities/form.entity';

import { GeologicalItem } from './geological-load-item';
import { GeologicalLoadRigOperation } from './geological-load-rig-operation';
import styles from './geological-load-tab.module.scss';
import { GeologicalLoadViewModel } from './geological-load-view-model';

type Props = {
  formStore: FormStore;
  directories: Directories;
};

export const GeologicalLoadTab = observer<Props>(function GeologicalLoadTab({ formStore, directories }) {
  const { t } = useTranslation();
  const [geologicalLoadViewModel] = useState(() => new GeologicalLoadViewModel(directories));
  const { geologicalLoadService, rigOperationsService } = formStore;

  const { geotaskGeoLoad, geoLoadRigOperations, validation, onValueChange } = geologicalLoadService;

  const [activePanels, setActivePanels] = useState(() => {
    if (!geoLoadRigOperations.length) {
      return [];
    }

    return geoLoadRigOperations.map((ro) => ro.entityId.toString());
  });

  useEffect(() => {
    setActivePanels(geoLoadRigOperations.map((ro) => ro.entityId.toString()));
  }, [geoLoadRigOperations]);

  if (!geoLoadRigOperations.length && !geotaskGeoLoad) {
    console.error('no geoLoadRigOperations and no geotaskGeoLoad');
    return null;
  }

  if (!geoLoadRigOperations.length && geotaskGeoLoad) {
    return (
      <div className={styles.wrapper}>
        <GeologicalItem
          geologicalLoad={geotaskGeoLoad}
          viewModel={geologicalLoadViewModel}
          validation={validation}
          onValueChange={(fieldId, value) => onValueChange(fieldId, value, {})}
        />
      </div>
    );
  }

  const handleChangeActivePanel = (keys: string | string[]) => {
    if (!Array.isArray(keys)) {
      setActivePanels([keys]);
      return;
    }

    setActivePanels(keys);
  };

  return (
    <div className={styles.wrapper}>
      <Collapse
        onChange={handleChangeActivePanel}
        activeKey={activePanels}
        className={styles.rigOperationsCollapse}
        expandIcon={({ isActive }) => {
          return (
            <div className={styles.expandIconContainer}>
              <DropdownIcon
                className={clsx(styles.expandIcon, isActive && styles.expandIconActive, styles.expandIconDisabled)}
              />
            </div>
          );
        }}
      >
        {geoLoadRigOperations.map((geoLoadRigOperation) => {
          const rigOperation = rigOperationsService.rigOperationsMap.get(geoLoadRigOperation.rigOperationEntityId);

          if (!rigOperation) {
            console.error('cannot find corresponding rigOperation for geoLoadRigOperaion', geoLoadRigOperation);
            return null;
          }

          return (
            <Collapse.Panel
              key={geoLoadRigOperation.entityId}
              header={
                <div className={styles.rigOperationPanelHeader}>
                  <p className={styles.rigOperationPanelHeader__text}>
                    {t('geologicalLoad:labels.rigOperationIndex', {
                      index: rigOperationsService.rigOperations.indexOf(rigOperation) + 1,
                    })}
                  </p>
                  <div className={styles.rigOperationPanelHeader__iconContainer}>
                    <InfoIcon />
                  </div>
                </div>
              }
              className={styles.rigOperationPanel}
            >
              <GeologicalLoadRigOperation
                geoLoadRigOperation={geoLoadRigOperation}
                rigOperation={rigOperation}
                // TODO: избавиться от этого пропа, когда притащим di-ez в проект
                viewModel={geologicalLoadViewModel}
                validation={validation}
                onValueChange={onValueChange}
              />
            </Collapse.Panel>
          );
        })}
      </Collapse>
    </div>
  );
});
